import { formatGlobalDiscountLabel } from '../../services/invoices.js';

const currencyFormatter = new Intl.NumberFormat('es-AR', {
  style: 'currency',
  currency: 'ARS',
  minimumFractionDigits: 2,
});

function formatCurrency(value) {
  return currencyFormatter.format(Number.isFinite(value) ? value : 0);
}

export default function TotalsBox({
  totals,
  globalDiscount,
  onGlobalDiscountChange,
  otherTaxes,
  onOtherTaxesChange,
  roundToHundred = false,
  onRoundToHundredChange,
  editable = false,
  className = '',
  title = 'Totales',
  embedded = false,
}) {
  const ivaEntries = Object.entries(totals?.ivaByRate || {});
  const discountValue = Number(globalDiscount?.value) || 0;
  const mode = globalDiscount?.mode || 'pct';
  const discountAmount = Number(totals?.discount) || 0;
  // Includes the rounding base so that subtotal - discount = net shown below.
  const subtotalBeforeDiscount = (Number(totals?.net) || 0) + discountAmount;
  const otherTaxesList = Array.isArray(otherTaxes) && otherTaxes.length > 0
    ? otherTaxes
    : [{ label: 'Otros impuestos', amount: 0 }];

  const handleModeChange = event => {
    const nextMode = event.target.value;
    onGlobalDiscountChange?.({ mode: nextMode, value: discountValue });
  };

  const handleDiscountValueChange = event => {
    const raw = event.target.value;
    const value = raw === '' ? '' : Number(raw);
    onGlobalDiscountChange?.({ mode, value: Number.isNaN(value) ? 0 : value });
  };

  const handleOtherTaxAmountChange = (index, value) => {
    if (!onOtherTaxesChange) {
      return;
    }
    const nextValue = value === '' ? '' : Number(value);
    const items = otherTaxesList.map((tax, idx) =>
      idx === index ? { ...tax, amount: Number.isNaN(nextValue) ? 0 : nextValue } : tax,
    );
    onOtherTaxesChange(items);
  };

  return (
    <div className={`invoice-totals${embedded ? ' invoice-totals--embedded' : ''}${className ? ` ${className}` : ''}`}>
      {title ? <h3 className="invoice-totals__title">{title}</h3> : null}
      {discountAmount > 0 ? (
        <div className="invoice-totals__row">
          <span>Subtotal sin IVA</span>
          <span>{formatCurrency(subtotalBeforeDiscount)}</span>
        </div>
      ) : null}
      <div className="invoice-totals__discount">
        <span>{formatGlobalDiscountLabel(mode === 'pct' && discountAmount > 0 ? discountValue : null)}</span>
        {editable ? (
          <div className="invoice-totals__controls">
            <select value={mode} onChange={handleModeChange}>
              <option value="pct">%</option>
              <option value="amount">$</option>
            </select>
            <input
              type="number"
              min="0"
              step="0.01"
              value={discountValue}
              onChange={handleDiscountValueChange}
            />
            <span
              className="invoice-totals__hint"
              title="Aplicado sobre el neto antes de impuestos."
              aria-label="Aplicado sobre el neto antes de impuestos."
            >
              sobre neto
            </span>
          </div>
        ) : (
          <span>{formatCurrency(discountAmount > 0 ? -discountAmount : 0)}</span>
        )}
      </div>
      <div className="invoice-totals__row">
        <span>Neto gravado</span>
        <strong>{formatCurrency(totals?.net || 0)}</strong>
      </div>
      {ivaEntries.map(([rate, entry]) => (
        <div key={rate} className="invoice-totals__row">
          <span>IVA {rate}%</span>
          <span>{formatCurrency(entry?.iva || 0)}</span>
        </div>
      ))}
      {otherTaxesList.map((tax, index) => (
        <div key={tax.label || index} className="invoice-totals__row invoice-totals__row--editable">
          <span>{tax.label || 'Otros impuestos'}</span>
          {editable ? (
            <input
              className="invoice-totals__inline-input"
              type="number"
              min="0"
              step="0.01"
              value={tax.amount ?? 0}
              onChange={event => handleOtherTaxAmountChange(index, event.target.value)}
            />
          ) : (
            <span>{formatCurrency(tax.amount || 0)}</span>
          )}
        </div>
      ))}
      {editable && onRoundToHundredChange ? (
        <label className="invoice-totals__row">
          <span>Redondear total a $100</span>
          <input
            type="checkbox"
            checked={roundToHundred}
            onChange={event => onRoundToHundredChange(event.target.checked)}
          />
        </label>
      ) : null}
      {roundToHundred && Number(totals?.baseRoundingAdjustment || 0) !== 0 ? (
        <div className="invoice-totals__row">
          <span>Ajuste por redondeo</span>
          <span>{formatCurrency(totals?.baseRoundingAdjustment || 0)}</span>
        </div>
      ) : null}
      <div className="invoice-totals__row" data-emphasis="true">
        <span>Total</span>
        <span>{formatCurrency(totals?.total || 0)}</span>
      </div>
    </div>
  );
}
